import { useCallback, useEffect, useMemo, useRef } from "react";
import { Form, OverlayTrigger, Tooltip } from "react-bootstrap";
import {
  BriefcaseFill,
  ChevronDown,
  ChevronUp,
  PeopleFill,
  PersonFill,
} from "react-bootstrap-icons";
import type { MiscContent } from "@/briefpoint-client";
import type { QuestionListItems } from "@/components/DocumentWizard/PropoundDocumentReview";
import { getPartyDisplay } from "@/components/DocumentWizard/SpecialInterrogatoriesStep/Selections/utils";
import { TagsList } from "@/components/TagsList";
import ReactLogo from "@/images/logo.svg?react";
import type { GroupedContent, JurisdictionResponse } from "@/lib/types";
import { highlightMatchingText } from "@/utils";
import styles from "./CheckmarkListDiscReq.module.scss";

type checkmarkListProps = {
  availableTags: Map<number, string> | undefined;
  showQuestions: string[];
  group: MiscContent[];
  groupedQuestions: GroupedContent;
  question: MiscContent;
  setShowQuestions: React.Dispatch<React.SetStateAction<string[]>>;
  k: string;
  index: number;
  checkedItems: QuestionListItems[];
  caSupCourt: JurisdictionResponse;
  tagSectionToOpen?: string;
  tagIdToFind?: number;
  tagSearch: string;
  setCheckedItems: React.Dispatch<React.SetStateAction<QuestionListItems[]>>;
  handleCheckboxChange: (
    id: string | undefined,
    checked: boolean,
    parentTag: string,
    content: string | null | undefined,
    question?: MiscContent,
  ) => void;
  openOrCloseQuestion: (key: string) => void;
  existingQuestion: (id: string | undefined) => boolean;
  caseTypeDisplay: (
    caseTypeId: string | null | undefined,
    jurisdiction: JurisdictionResponse | undefined,
    includeClass: boolean,
    noCaseText?: string,
  ) => string | null | undefined;
};

const CheckmarkListDiscReq = ({
  availableTags,
  showQuestions,
  group,
  groupedQuestions,
  question,
  k,
  index,
  checkedItems,
  caSupCourt,
  tagSectionToOpen,
  tagIdToFind,
  tagSearch,
  setShowQuestions,
  handleCheckboxChange,
  openOrCloseQuestion,
  existingQuestion,
  caseTypeDisplay,
  setCheckedItems,
}: checkmarkListProps) => {
  const processedTagSectionRef = useRef<string | null>(null);
  const rowRef = useRef<HTMLTableRowElement>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: ignore groupedQuestions
  const handleTagSectionOpen = useCallback(() => {
    const shouldProcess =
      (tagSectionToOpen &&
        processedTagSectionRef.current !== tagSectionToOpen) ||
      tagIdToFind;

    if (!shouldProcess) return;

    // Determine the search criteria based on whether we're looking for a tag ID or section
    const searchPredicate = tagIdToFind
      ? ([_, questions]: [string, MiscContent[]]) =>
          questions.some((q) => q.tagIds?.includes(tagIdToFind))
      : ([_, questions]: [string, MiscContent[]]) =>
          questions.some((q) => q.id === tagSectionToOpen);

    const findIndexOfId =
      Object.entries(groupedQuestions).findIndex(searchPredicate);
    const activeQuestion = Object.keys(groupedQuestions)[findIndexOfId];

    if (activeQuestion) {
      processedTagSectionRef.current = tagSectionToOpen ?? null;
      setShowQuestions([activeQuestion]);

      // Add a small delay to ensure the DOM has updated before scrolling
      setTimeout(() => {
        if (rowRef.current && activeQuestion === k) {
          rowRef.current.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      }, 100);
    }
    // tagSectionToOpen/tagToFind is the only dependency here, groupedQuestions and setShowQuestions change frequently in this
    // flow, so to prevent any unnecessary re-renders we don't need to need to include them in the dependency array
    // eslint-disable-next-line
  }, [tagSectionToOpen, k, tagIdToFind]);

  // This UE fires off if a valid tagSectionToOpen is provided to the component, and it will open the
  // section of the library that contains corresponding to tag ID the tagSectionToOpen is set to.
  useEffect(() => {
    handleTagSectionOpen();
  }, [handleTagSectionOpen]);

  // Counter for showing selected requests for each request type
  const groupCheckedCount = group.filter(
    (q) =>
      checkedItems.some((item) => item.id === q.id) || existingQuestion(q.id),
  ).length;

  const handleIndividualCheck = (
    id: string | undefined,
    checked: boolean,
    parentTag: string,
    content?: string | null | undefined,
    question?: MiscContent,
  ) => {
    handleCheckboxChange(id, checked, parentTag, content, question);
  };

  // Function to check if ALL items in the group have been "checked"
  const areAllItemsChecked = (group: MiscContent[]) => {
    return group.every(
      (item) =>
        checkedItems.some((checkedItem) => checkedItem.id === item.id) ||
        existingQuestion(item.id),
    );
  };

  // Helper function to filter questions based on search term
  const filteredQuestions = useMemo(() => {
    return group.filter((q) => {
      if (!q.content || !tagSearch) return true;
      const searchRegex = new RegExp(`\\b${tagSearch.toLowerCase()}`, "i");
      return searchRegex.test(q.content.toLowerCase());
    });
  }, [group, tagSearch]);

  const handleSelectAllChange = () => {
    if (areAllItemsChecked(group)) {
      // Deselecting all checkboxes for this group
      setCheckedItems((prevItems) =>
        prevItems.filter(
          (item) =>
            !group.some((q) => q.id === item.id) || existingQuestion(item.id),
        ),
      );
    } else {
      // Selecting all unchecked checkboxes for this group
      setCheckedItems((prevItems) => {
        const newItems = group
          .filter(
            (q) =>
              !existingQuestion(q.id) &&
              !prevItems.some((item) => item.id === q.id),
          )
          .map((q) => ({
            id: q.id,
            content: q.content,
            checked: true,
            parentTag: k,
            question: q,
          }));

        return [...prevItems, ...newItems];
      });
    }
  };

  return (
    <>
      <tr className={styles.tableRowMain} key={index} ref={rowRef}>
        <td className={styles.questionFrom}>
          {question.sharedWithFirmId ? (
            <BriefcaseFill title="Firm Standard" />
          ) : (
            !question.ownerId && <ReactLogo title="Briefpoint Default" />
          )}
        </td>
        <td className={styles.tableCell}>
          {caseTypeDisplay(question.caseTypeId, caSupCourt, true)}
        </td>
        <td className={styles.tableCell}>
          <TagsList
            tagIds={question.tagIds}
            availableTags={availableTags}
            questionId={k}
            startTrimmingTagsAfter={3}
          />
        </td>
        <td className={styles.tableCell}>
          {question.respondentType === undefined
            ? ""
            : getPartyDisplay(question.respondentType, false)}
        </td>
        <td className={styles.tableCell} style={{ textAlign: "center" }}>
          {question.isForRepresentative === undefined ? (
            ""
          ) : question.isForRepresentative === true ? (
            <PeopleFill title="Representative" />
          ) : (
            <PersonFill title="Individual" />
          )}
        </td>
        <td className={styles.tableCell} style={{ textAlign: "center" }}>
          {groupCheckedCount ? (
            <p className={styles.existingQuestionsHighlight}>
              {groupCheckedCount}
            </p>
          ) : (
            ""
          )}
        </td>
        <td className={styles.tableCell} style={{ textAlign: "center" }}>
          {group.length}
        </td>
        <td className={`${styles.actionsCell} ${styles.tableCell}`}>
          {showQuestions.includes(k) ? (
            <ChevronUp
              title="Hide Questions"
              onClick={() => openOrCloseQuestion(k)}
            />
          ) : (
            <ChevronDown
              title="Show Questions"
              onClick={() => openOrCloseQuestion(k)}
            />
          )}
        </td>
      </tr>
      <tr className={styles.tableRow} key={`${k}-content`}>
        {showQuestions.includes(k) && (
          <>
            <td style={{ width: "15px" }}>&nbsp;</td>
            <td
              className={`${styles.tableCell} ${styles.noPadding}`}
              colSpan={6}
            >
              <div className={styles.libraryQuestionContainer}>
                {!tagSearch && group.some((q) => !existingQuestion(q.id)) && (
                  // biome-ignore lint/a11y/useKeyWithClickEvents: TODO: replace with button
                  <span
                    className={styles.selectAllTxt}
                    onClick={handleSelectAllChange}
                  >
                    <br />
                    {areAllItemsChecked(group) ? "Deselect All" : "Select All"}
                  </span>
                )}
                {filteredQuestions.map((q, i) => (
                  <div key={i} className={styles.questionsList}>
                    <div className={styles.questionsListItem}>
                      <div className={styles.questionsListCheck}>
                        {!existingQuestion(q.id) && (
                          <Form.Check id={`checkbox-${q.id}`}>
                            <Form.Check.Input
                              type="checkbox"
                              checked={checkedItems.some(
                                (item) => item.id === q.id,
                              )}
                              onChange={(e) =>
                                handleIndividualCheck(
                                  q.id,
                                  e.currentTarget.checked,
                                  k,
                                  q.content,
                                  q,
                                )
                              }
                              disabled={existingQuestion(q.id)}
                            />
                            <Form.Check.Label
                              htmlFor={`checkbox-${q.id}`}
                              className={styles.questionsText}
                            >
                              {highlightMatchingText(
                                q.content || "",
                                tagSearch,
                              )}
                            </Form.Check.Label>
                          </Form.Check>
                        )}
                      </div>
                      {existingQuestion(q.id) && (
                        <div className={styles.questionsText}>
                          <OverlayTrigger
                            overlay={
                              <Tooltip className="text-capitalize">
                                Already included
                              </Tooltip>
                            }
                          >
                            <p className={styles.exists}>
                              {highlightMatchingText(
                                q.content || "",
                                tagSearch,
                              )}
                            </p>
                          </OverlayTrigger>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </td>
          </>
        )}
      </tr>
    </>
  );
};

export default CheckmarkListDiscReq;
